import React, { useState, useEffect } from 'react';
import { Genre, Book, SearchResult, BookCheckResult, Language } from './types';
import { findBooksByGenre, checkBookNYTStatus } from './services/geminiService';
import GenreSelector from './components/GenreSelector';
import BookCard from './components/BookCard';
import SourceList from './components/SourceList';

type Theme = 'light' | 'dark';
type Tab = 'genre' | 'search';

const App: React.FC = () => {
  // Config State
  const [language, setLanguage] = useState<Language>('fa');
  const [theme, setTheme] = useState<Theme>('light');
  const [activeTab, setActiveTab] = useState<Tab>('genre');

  // Search Data State
  const [selectedGenre, setSelectedGenre] = useState<Genre | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Results State
  const [books, setBooks] = useState<Book[]>([]); // For Genre Search
  const [checkResult, setCheckResult] = useState<BookCheckResult | null>(null); // For Specific Search
  const [sources, setSources] = useState<string[]>([]);
  
  // UI State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  // Effect to handle document dir/lang/theme changes
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'fa' ? 'rtl' : 'ltr';
  }, [language]);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fa' ? 'en' : 'fa');
    // Reset states on language change to clear mismatched text
    setBooks([]);
    setCheckResult(null);
    setSources([]);
    setHasSearched(false);
    setError(null);
  };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    setHasSearched(false);
    setError(null);
    setSources([]);
  };

  // Genre Search Handler
  const handleGenreSearch = async () => {
    if (!selectedGenre) return;

    setIsLoading(true);
    setError(null);
    setBooks([]);
    setSources([]);
    setCheckResult(null);
    setHasSearched(true);

    try {
      const result: SearchResult = await findBooksByGenre(selectedGenre, language);
      setBooks(result.books);
      setSources(result.sources);
    } catch (err: any) {
      setError(err.message || (language === 'fa' ? 'خطایی در دریافت اطلاعات رخ داد.' : 'Error fetching data.'));
    } finally {
      setIsLoading(false);
    }
  };

  // Specific Book Search Handler
  const handleSpecificSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    setError(null);
    setBooks([]);
    setCheckResult(null);
    setSources([]);
    setHasSearched(true);

    try {
      const result: BookCheckResult = await checkBookNYTStatus(searchQuery, language);
      setCheckResult(result);
      setSources(result.sources);
    } catch (err: any) {
      setError(err.message || (language === 'fa' ? 'خطایی در بررسی کتاب رخ داد.' : 'Error checking book status.'));
    } finally {
      setIsLoading(false);
    }
  };

  // UI Translations
  const t = {
    title: { fa: 'کتاب‌یاب', en: 'Book Finder' },
    heroTitle: { fa: 'بهترین کتاب‌های پرفروش را پیدا کنید', en: 'Find Top Best Sellers' },
    heroDesc: { 
      fa: 'با هوش مصنوعی، لیست کتاب‌های برتر نیویورک تایمز در ۱۵ سال گذشته را بررسی کنید یا وضعیت یک کتاب خاص را جستجو نمایید.',
      en: 'Explore top New York Times Best Sellers from the last 15 years or check if a specific book made the list.'
    },
    tabGenre: { fa: 'جستجو بر اساس ژانر', en: 'Browse by Genre' },
    tabSearch: { fa: 'جستجو بر اساس نام/نویسنده', en: 'Search by Title/Author' },
    inputPlaceholder: { fa: 'نام کتاب یا نویسنده را وارد کنید...', en: 'Enter book title or author...' },
    searchBtn: { fa: 'جستجو', en: 'Search' },
    checkBtn: { fa: 'بررسی', en: 'Check Status' },
    loading: { fa: 'در حال پردازش...', en: 'Processing...' },
    resultsTitle: { fa: 'نتایج پیشنهادی', en: 'Recommended Books' },
    errorTitle: { fa: 'خطا', en: 'Error' },
    emptyState: { fa: 'موردی یافت نشد.', en: 'No results found.' },
    notBestSeller: { fa: 'این مورد در لیست پرفروش‌های ۱۵ سال اخیر یافت نشد.', en: 'Not found in the Best Seller lists of the last 15 years.' }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-gray-800 dark:text-gray-100 font-sans transition-colors duration-300 pb-12">
      {/* Header */}
      <header className="bg-white dark:bg-slate-950 border-b border-gray-200 dark:border-slate-800 sticky top-0 z-10 transition-colors duration-300">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-black dark:bg-white rounded-full flex items-center justify-center text-white dark:text-black font-serif font-bold text-xl transition-colors">T</div>
            <h1 className="text-xl md:text-2xl font-black tracking-tight text-gray-900 dark:text-white">
              {t.title[language]} <span className="text-gray-500 font-serif italic">NYTimes</span>
            </h1>
          </div>
          
          <div className="flex items-center gap-3">
             <button 
              onClick={toggleLanguage}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-sm font-bold text-gray-600 dark:text-gray-300 border border-transparent hover:border-gray-200 dark:hover:border-slate-700 transition-all"
            >
              {language === 'fa' ? 'EN' : 'فارسی'}
            </button>

            <button 
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-600 dark:text-yellow-400 transition-colors"
            >
              {theme === 'light' ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 mt-8">
        
        {/* Hero Section */}
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white leading-tight transition-colors">
            {t.heroTitle[language]}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-lg transition-colors">
            {t.heroDesc[language]}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-6">
          <div className="bg-gray-100 dark:bg-slate-800 p-1 rounded-xl inline-flex shadow-inner">
            <button
              onClick={() => handleTabChange('genre')}
              className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${
                activeTab === 'genre'
                  ? 'bg-white dark:bg-slate-600 text-black dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {t.tabGenre[language]}
            </button>
            <button
              onClick={() => handleTabChange('search')}
              className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${
                activeTab === 'search'
                  ? 'bg-white dark:bg-slate-600 text-black dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {t.tabSearch[language]}
            </button>
          </div>
        </div>

        {/* Search Controls Area */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 mb-10 transition-colors min-h-[200px] flex flex-col justify-center">
          
          {/* CONTENT FOR GENRE TAB */}
          {activeTab === 'genre' && (
            <div className="animate-fade-in">
              <GenreSelector 
                selectedGenre={selectedGenre} 
                onSelect={setSelectedGenre} 
                isLoading={isLoading}
                language={language}
              />
              <div className="mt-8 flex justify-center">
                <button
                  onClick={handleGenreSearch}
                  disabled={!selectedGenre || isLoading}
                  className={`
                    px-8 py-3 rounded-full text-lg font-bold shadow-lg transition-all duration-300 flex items-center gap-2
                    ${!selectedGenre || isLoading 
                      ? 'bg-gray-300 dark:bg-slate-700 text-gray-500 dark:text-gray-400 cursor-not-allowed shadow-none' 
                      : 'bg-black dark:bg-indigo-600 text-white hover:bg-gray-800 dark:hover:bg-indigo-500 hover:scale-105 hover:shadow-xl ring-4 ring-gray-100 dark:ring-slate-700'}
                  `}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{t.loading[language]}</span>
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${language === 'en' ? 'order-last' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span>{t.searchBtn[language]}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* CONTENT FOR SPECIFIC SEARCH TAB */}
          {activeTab === 'search' && (
            <div className="animate-fade-in w-full max-w-2xl mx-auto">
              <form onSubmit={handleSpecificSearch} className="flex flex-col md:flex-row gap-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t.inputPlaceholder[language]}
                  disabled={isLoading}
                  className={`flex-grow px-5 py-4 rounded-xl border-2 bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${language === 'fa' ? 'text-right' : 'text-left'} ${isLoading ? 'opacity-60' : 'border-gray-200 dark:border-slate-600'}`}
                />
                <button
                  type="submit"
                  disabled={!searchQuery.trim() || isLoading}
                  className={`
                    px-8 py-4 rounded-xl font-bold shadow-md transition-all duration-300 flex justify-center items-center whitespace-nowrap
                    ${!searchQuery.trim() || isLoading
                      ? 'bg-gray-300 dark:bg-slate-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      : 'bg-black dark:bg-indigo-600 text-white hover:bg-gray-800 dark:hover:bg-indigo-500 hover:scale-105'}
                  `}
                >
                  {isLoading ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <span>{t.checkBtn[language]}</span>
                  )}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border-r-4 dark:border-r-0 dark:border-l-4 border-red-500 p-4 mb-8 rounded-lg animate-fade-in">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="mx-3">
                <p className="text-sm text-red-700 dark:text-red-300 font-bold">{t.errorTitle[language]}</p>
                <p className="text-sm text-red-600 dark:text-red-200 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* --- DISPLAY RESULTS --- */}
        
        {/* 1. Genre Search Results */}
        {activeTab === 'genre' && !isLoading && books.length > 0 && (
          <div className="animate-fade-in-up">
            <div className="flex justify-between items-end mb-6 border-b border-gray-200 dark:border-slate-700 pb-2">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t.resultsTitle[language]}
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {books.map((book, index) => (
                <BookCard key={index} book={book} rank={index + 1} language={language} />
              ))}
            </div>
            <SourceList sources={sources} language={language} />
          </div>
        )}

        {/* 2. Specific Book/Author Check Result */}
        {activeTab === 'search' && !isLoading && checkResult && (
          <div className="animate-fade-in-up max-w-4xl mx-auto">
             {checkResult.isBestSeller && checkResult.books.length > 0 ? (
                // SUCCESS: Books Found
                <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-2xl border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-green-100 dark:bg-green-800 p-2 rounded-full flex-shrink-0">
                       <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600 dark:text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                       </svg>
                    </div>
                    <h3 className="text-xl font-bold text-green-800 dark:text-green-200 leading-snug">
                      {checkResult.message}
                    </h3>
                  </div>
                  
                  {/* Grid of Found Books (Handles single book or multiple from author) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {checkResult.books.map((book, idx) => (
                      <div key={idx} className="transform hover:scale-102 transition-transform duration-300 h-full">
                        <BookCard book={book} rank={idx + 1} language={language} />
                      </div>
                    ))}
                  </div>
                </div>
             ) : (
                // FAILURE: Nothing Found
                <div className="bg-gray-50 dark:bg-slate-800 p-8 rounded-2xl border border-gray-200 dark:border-slate-700 text-center">
                   <div className="inline-block p-4 bg-gray-100 dark:bg-slate-700 rounded-full mb-4">
                     <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                     </svg>
                   </div>
                   <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">{checkResult.message}</h3>
                   <p className="text-gray-500 dark:text-gray-400">{t.notBestSeller[language]}</p>
                </div>
             )}
             <SourceList sources={sources} language={language} />
          </div>
        )}

        {/* Empty State Logic */}
        {!isLoading && hasSearched && !error && (
            (activeTab === 'genre' && books.length === 0) 
        ) && (
          <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-xl border border-dashed border-gray-300 dark:border-slate-600">
            <p className="text-gray-500 dark:text-gray-400 text-lg">{t.emptyState[language]}</p>
          </div>
        )}

      </main>
    </div>
  );
};

export default App;